import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import connectApi from 'api/connectApi'
import {
    hasLocalDataExpired,
    updateLocalDataExpirationTime
} from 'datastores/LocalDataExpiration'
import {
    constants,
    internetConnect,
    sendMessageToWorker,
    sendMessageToWorkerWithResponse,
    VillageType
} from 'helpers'
import { Order } from 'storeTypes'

type ClientOrdersState = {
    clientOrders: Order[]
}

type VillageOrdersItem = {
    clientOrders?: Order[]
    loading?: boolean
    error?: string
}

type VillageClientOrdersState = Record<string, VillageOrdersItem>

export const getClientOrdersAction = createAsyncThunk<
    Order[],
    string,
    { rejectValue: undefined }
>(
    'clientOrders/getClientOrdersAction',
    async (clientCode, { rejectWithValue }) => {
        try {
            const { result } = await sendMessageToWorkerWithResponse({
                'get client orders': clientCode
            })
            return result as Order[]
        } catch {
            return rejectWithValue(undefined)
        }
    }
)

export const storeVillageOrdersAction = createAsyncThunk<
    void,
    VillageType[],
    { rejectValue: string }
>(
    'villageClientOrders/storeVillageOrdersAction',
    async (villages, { dispatch, rejectWithValue }) => {
        const { CLIENT_ORDERS } = constants.endpoints
        const villageIdsToUpdate: string[] = []

        try {
            const internetConnection = internetConnect()
            villages.forEach((village) => {
                if (
                    hasLocalDataExpired(
                        constants.CLIENT_LIST_EXPIRATION_NAME,
                        village.sf_id
                    )
                ) {
                    villageIdsToUpdate.push(village.sf_id)
                }
            })

            if (!internetConnection || villageIdsToUpdate.length === 0) return

            villageIdsToUpdate.forEach((villageId) => {
                dispatch(storingOrders({ villageId }))
            })

            const params =
                villageIdsToUpdate
                    .map((villageId) => `village_ids=${villageId}`)
                    .join('&') + '&active_only=true'
            const response = await connectApi.get(`${CLIENT_ORDERS}?${params}`)

            if (response.status !== 200) {
                throw new Error('Response status ' + response.status)
            }

            villageIdsToUpdate.forEach((villageId) =>
                updateLocalDataExpirationTime(
                    constants.CLIENT_LIST_EXPIRATION_NAME,
                    villageId
                )
            )
            sendMessageToWorker({
                'store client orders': response.data.data
            })
            villageIdsToUpdate.forEach((villageId) => {
                dispatch(
                    storeOrders({
                        villageId,
                        clientOrders: response.data.data
                    })
                )
            })
        } catch (error: unknown) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error'
            villageIdsToUpdate.forEach((villageId) => {
                dispatch(
                    failStoreVillageOrders({
                        villageId,
                        error: errorMessage
                    })
                )
            })
            return rejectWithValue(errorMessage)
        }
    }
)

const clientOrdersInitialState: ClientOrdersState = { clientOrders: [] }
const villageClientOrdersInitialState: VillageClientOrdersState = {}

const clientOrdersSlice = createSlice({
    name: 'clientOrders',
    initialState: clientOrdersInitialState,
    reducers: {
        resetClientOrdersAction: () => ({ clientOrders: [] })
    },
    extraReducers: (builder) => {
        builder
            .addCase(getClientOrdersAction.fulfilled, (_, action) => ({
                clientOrders: action.payload
            }))
            .addCase(getClientOrdersAction.rejected, () => ({
                clientOrders: []
            }))
    }
})

const villageClientOrdersSlice = createSlice({
    name: 'villageClientOrders',
    initialState: villageClientOrdersInitialState,
    reducers: {
        storingOrders: (
            state,
            action: PayloadAction<{ villageId: string }>
        ) => {
            const { villageId } = action.payload
            state[villageId] = {
                ...state[villageId],
                loading: true,
                error: ''
            }
        },
        storeOrders: (
            state,
            action: PayloadAction<{
                villageId: string
                clientOrders: Order[]
            }>
        ) => {
            const { villageId, clientOrders } = action.payload
            state[villageId] = {
                ...state[villageId],
                loading: false,
                error: '',
                clientOrders
            }
        },
        failStoreVillageOrders: (
            state,
            action: PayloadAction<{ villageId: string; error: string }>
        ) => {
            const { villageId, error } = action.payload
            state[villageId] = {
                ...state[villageId],
                loading: false,
                clientOrders: [],
                error
            }
        }
    }
})

export const { resetClientOrdersAction } = clientOrdersSlice.actions
export const { storingOrders, storeOrders, failStoreVillageOrders } =
    villageClientOrdersSlice.actions
export const clientOrdersReducer = clientOrdersSlice.reducer
export const villageClientOrdersReducer = villageClientOrdersSlice.reducer
